'use client'

import { Sparkles } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'

interface GenerateButtonProps {
  onClick: () => void | Promise<void>
  disabled?: boolean
  requesting?: boolean
  className?: string
  icon?: React.ReactNode
  text?: string
  showFreeLabel?: boolean
}

/**
 * Unified Generate button component for HeroPrompt
 * Used across New idea, Clone website, and Redesign old site modes
 */
export function GenerateButton({
  onClick,
  disabled = false,
  requesting = false,
  className,
  icon,
  text,
  showFreeLabel = false,
}: GenerateButtonProps) {
  const t = useTranslations('home_v3.hero')
  const displayText = text || t('generate')

  return (
    <div className="flex flex-col items-end gap-1.5">
      {showFreeLabel && (
        <div className="flex items-center gap-1.5 text-[11px] text-green-400 font-medium">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span>{t('free_first_version')}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5 bg-gold-gradient rounded-lg text-black font-semibold text-sm sm:text-base hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0',
          className,
        )}
      >
        {requesting ? (
          icon ? (
            <span className="animate-spin">{icon}</span>
          ) : (
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
          )
        ) : icon ? (
          icon
        ) : (
          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
        )}
        <span>{displayText}</span>
      </button>
    </div>
  )
}
